import { ForwardedRef, forwardRef, useContext } from 'react'
import Artboard, { GIF, Image, Sprite, Text } from '../../artboard-gl'
import {
    ArtboardGifLayerProps,
    ArtboardImageLayerProps,
    ArtboardRef,
    ArtboardTextLayerProps,
} from '../../artboard-gl/types'
import { UGEContext } from '../context'

const UGEExportArtboard = (_: unknown, ref: ForwardedRef<ArtboardRef>) => {
    const {
        dimensions,
        duration,
        enableStickers,
        filter,
        filterIntensity,
        layerPositions,
        layers,
        reversed,
        sourceInfo,
        sourceSprite,
        trim,
    } = useContext(UGEContext)
    if (!sourceInfo) {
        return null
    }

    return (
        // Keep the export renderer paintable on iOS; fully hidden WebGL canvases can capture blank frames.
        <div
            aria-hidden
            style={{
                height: dimensions.height,
                left: 0,
                opacity: 0.001,
                overflow: 'hidden',
                pointerEvents: 'none',
                position: 'absolute',
                top: 0,
                width: dimensions.width,
                zIndex: -1,
            }}
        >
            <Artboard
                desynchronized={false}
                disableLoop
                duration={duration}
                filter={filter}
                filterIntensity={filterIntensity}
                fps={sourceInfo.fps}
                height={dimensions.height}
                preserveDrawingBuffer
                ref={ref}
                transparency={enableStickers}
                trim={trim}
                width={dimensions.width}
            >
                <Sprite height={dimensions.height} reversed={reversed} source={sourceSprite} width={dimensions.width} />
                {layers.map((layer) => {
                    const { props, type } = layer
                    const layerKey = `uge-export-artboard-layer-${layer.id}`
                    const position = layerPositions[layer.id]
                    const positionedProps = position ? { ...props, ...position } : props
                    const defaultProps = {
                        draggable: false,
                        persistFocus: false,
                        x: Math.round(dimensions.width / 2 - props.width / 2),
                    }

                    switch (type) {
                        case 'gif':
                        case 'sticker':
                            return (
                                <GIF key={layerKey} {...defaultProps} {...(positionedProps as ArtboardGifLayerProps)} />
                            )
                        case 'image':
                            return (
                                <Image
                                    key={layerKey}
                                    {...defaultProps}
                                    {...(positionedProps as ArtboardImageLayerProps)}
                                />
                            )
                        case 'text':
                            return (
                                <Text
                                    key={layerKey}
                                    {...defaultProps}
                                    {...(positionedProps as ArtboardTextLayerProps)}
                                />
                            )
                        default:
                            return null
                    }
                })}
            </Artboard>
        </div>
    )
}

export default forwardRef(UGEExportArtboard)
